import { useState, useEffect, useRef } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import './MainLayout.css';

// Hooks
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { useDailyTasks } from '../hooks/useDailyTasks';
import { useChatSystem } from '../hooks/useChat';

// Layout components
import Header from './Header';
import Footer from './Footer';
import NotificationPanel from './panels/NotificationPanel';
import TaskPanel from './panels/TaskPanel';
import MessagesPanel from './panels/MessagesPanel';

// Pages
import AuthPage from '../pages/AuthPage';
import Modal from '../components/ui/Modal';
import BottomBar from '../components/BottomBar';
import FloatingAddMenu from '../components/FloatingAddMenu';

// Icons
import { FiGrid, FiClock, FiTarget, FiMessageSquare, FiUsers } from 'react-icons/fi';

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Auth ──
  const {
    userId, username, isLoggedIn, isAdmin, isCoach, isPremium, coachId,
    avatar, xp, level, badges, xpPerLevel, xpProgress,
    setXp, setLevel, setBadges,
  } = useAuth();

  // ── Notifications ──
  const {
    notifications, hasUnread, markAllRead,
    getNotificationText, getNotificationIcon,
  } = useNotifications(userId);

  // ── Daily Tasks ──
  const { tasks, rewardsClaimed, completeTask } = useDailyTasks(
    userId,
    { xp, badges, setXp, setLevel, setBadges }
  );

  // ── Chat ──
  const chat = useChatSystem(userId, isPremium);

  // ── UI State ──
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [taskPanelOpen, setTaskPanelOpen] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('all');
  
  const [loggingOut, setLoggingOut] = useState(false);

  // Footer modals
  const [aboutOpen, setAboutOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Feedback
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackDesc, setFeedbackDesc] = useState('');

  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const taskRef = useRef(null);
  const msgRef = useRef(null);

  // ── Onboarding ──
  const onboardingSteps = [
    { title: 'Hoş Geldin! 🚀', description: 'Yelken, hedeflerine ulaşman için tasarlandı.' },
    { title: 'Kronometre ⏱️', description: 'Çalışma sürenizi takip edin ve kaydedin.' },
    { title: 'Hedefler 🎯', description: 'Günlük ve haftalık hedefler belirleyin.' },
    { title: 'Topluluk 🤝', description: 'Forum ve sıralamalarla diğer öğrencilerle etkileşime geçin.' },
  ];

  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', userId));
        if (snap.exists() && !snap.data().hideOnboarding) setShowOnboarding(true);
      } catch {}
    })();
  }, [isLoggedIn, userId]);

  // ── Click outside ──
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotificationOpen(false);
      if (taskRef.current && !taskRef.current.contains(e.target)) setTaskPanelOpen(false);
      if (msgRef.current && !msgRef.current.contains(e.target)) {
        setMessagesOpen(false);
        chat.closeChat();
        
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [chat]);

  // ── Handlers ──
  const closeAllPanels = () => {
    setDropdownOpen(false);
    setNotificationOpen(false);
    setTaskPanelOpen(false);
    setMessagesOpen(false);
    chat.closeChat();
    
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await signOut(auth);
    localStorage.clear();
    setTimeout(() => { 
      setLoggingOut(false); 
      window.location.href = '/'; 
    }, 1500);
  };

  const handleNotificationClick = async (e) => {
    e.stopPropagation();
    closeAllPanels();
    setNotificationOpen((v) => !v);
    if (!notificationOpen) await markAllRead();
  };

  const handleNotificationRedirect = (n) => {
    setNotificationOpen(false);
    if (['like', 'comment', 'mention'].includes(n.type) && n.postId) navigate(`/forum/${n.postId}`);
    else if (n.type === 'follow') navigate(`/user/${n.fromUserId}`);
    else navigate('/account');
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackTitle.trim() || !feedbackDesc.trim()) { toast.error('Tüm alanları doldurun.'); return; }
    try {
      await addDoc(collection(db, 'feedbacks'), {
        userId: userId || 'anonymous', username: username || 'anonymous',
        type: feedbackType, title: feedbackTitle.trim(), description: feedbackDesc.trim(),
        timestamp: new Date(), status: 'pending',
      });
      toast.success('Geri bildiriminiz gönderildi!');
      setFeedbackTitle(''); setFeedbackDesc(''); setFeedbackOpen(false);
    } catch { toast.error('Gönderilemedi.'); }
  };

  const handleOnboardingClose = async (permanent = false) => {
    setShowOnboarding(false);
    if (permanent && userId) {
      try { await updateDoc(doc(db, 'users', userId), { hideOnboarding: true }); } catch {}
    }
  };

  // ── Logout overlay ──
  if (loggingOut) {
    return (
      <div className="logout-overlay">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}>
          <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>Çıkış Yapılıyor...</h2>
          <p className="text-muted" style={{ marginTop: 'var(--space-2)' }}>Görüşmek üzere!</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Header
        isLoggedIn={isLoggedIn} username={username} avatar={avatar}
        isPremium={isPremium} isAdmin={isAdmin} isCoach={isCoach} coachId={coachId} hasUnread={hasUnread}
        onAuthModalOpen={() => setAuthModalOpen(true)}
        onNotificationClick={handleNotificationClick}
        onMessagesClick={(e) => {
          e.stopPropagation(); closeAllPanels();
          setMessagesOpen((v) => !v);
        }}
        onTaskPanelClick={(e) => { e.stopPropagation(); closeAllPanels(); setTaskPanelOpen((v) => !v); }}
        onAvatarClick={(e) => { e.stopPropagation(); closeAllPanels(); setDropdownOpen((v) => !v); }}
        dropdownOpen={dropdownOpen} dropdownRef={dropdownRef}
        xpProgress={xpProgress} xp={xp} xpPerLevel={xpPerLevel} level={level}
        onLogout={handleLogout} onNavigate={navigate}
      >
        {/* Dropdown */}
        <AnimatePresence>
          {dropdownOpen && (
            <motion.div className="panel auth-dropdown" key="auth-dropdown" ref={dropdownRef}
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            >
              {/* isPremium && (
                <div className="dropdown-premium">
                  <span className="text-gradient font-semibold">✨ Premium</span>
                  <button className="dropdown-item" onClick={() => navigate('/premium-benefits')}>Premium Avantajlarım</button>
                </div>
              ) */}
              <div className="dropdown-level">
                <span className="badge badge-accent">Seviye {level}</span>
                <div className="dropdown-xp-bar">
                  <motion.div className="dropdown-xp-fill" initial={{ width: 0 }} animate={{ width: `${xpProgress}%` }} />
                </div>
                <span className="text-xs text-muted">{xp % xpPerLevel}/{xpPerLevel} XP</span>
              </div>
              <button className="dropdown-item" onClick={() => navigate('/account')}>👤 Hesabım</button>
              <button className="dropdown-item dropdown-item--danger" onClick={handleLogout}>🚪 Çıkış Yap</button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Panels */}
        <NotificationPanel
          ref={notifRef} isOpen={notificationOpen} notifications={notifications}
          filter={notificationFilter} setFilter={setNotificationFilter}
          onRedirect={handleNotificationRedirect}
          getNotificationText={getNotificationText} getNotificationIcon={getNotificationIcon}
        />
        <TaskPanel ref={taskRef} isOpen={taskPanelOpen} tasks={tasks} rewardsClaimed={rewardsClaimed} onComplete={completeTask} />
        {/* <MessagesPanel
          ref={msgRef} isOpen={messagesOpen} 
          chats={chat.chats} selectedChat={chat.selectedChat} messages={chat.messages}
          text={chat.text} setText={chat.setText} chatError={chat.chatError}
          searchQuery={chat.searchQuery} searchResults={chat.searchResults} chatBoxRef={chat.chatBoxRef}
          onSend={chat.sendMessage} onSearch={chat.searchUsers}
          onSelectChat={chat.setSelectedChat} onStartChat={chat.startChat}
          onDeleteChat={chat.deleteChat}
          onClose={() => { setMessagesOpen(false); chat.closeChat();  }}
        /> */}
      </Header>

      {/* Main Content */}
      <motion.main
        className="content"
        key={location.pathname}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Outlet context={{ isPremium }} />
      </motion.main>

      <Footer
        onAbout={() => setAboutOpen(true)} onContact={() => setContactOpen(true)}
        onPrivacy={() => setPrivacyOpen(true)} onTerms={() => setTermsOpen(true)}
        onAuth={() => setAuthModalOpen(true)}
        onFeedback={() => setFeedbackOpen(true)}
      />

      {/* MOBILE BOTTOM BAR (REMOVED AND REPLACED WITH FAB) */}
      {isLoggedIn && <FloatingAddMenu />}

      {/* Auth Modal */}
      <Modal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} title="Giriş Yap / Kayıt Ol" maxWidth="420px">
        <AuthPage inModal onClose={() => setAuthModalOpen(false)} />
      </Modal>

      {/* Feedback Modal */}
      <Modal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} title="Geri Bildirim">
        <form onSubmit={handleFeedbackSubmit} className="flex flex-col gap-4">
          <select className="input" value={feedbackType} onChange={(e) => setFeedbackType(e.target.value)}>
            <option value="suggestion">Öneri</option>
            <option value="bug">Hata Bildirimi</option>
            <option value="other">Diğer</option>
          </select>
          <input className="input" placeholder="Başlık" value={feedbackTitle} onChange={(e) => setFeedbackTitle(e.target.value)} />
          <textarea className="input" placeholder="Açıklama" value={feedbackDesc} onChange={(e) => setFeedbackDesc(e.target.value)} rows={4} style={{ resize: 'vertical', minHeight: '100px' }} />
          <button type="submit" className="btn btn-primary">Gönder</button>
        </form>
      </Modal>

      {/* Info Modals (About, Contact, Privacy, Terms) */}
      <Modal isOpen={aboutOpen} onClose={() => setAboutOpen(false)} title="Hakkımızda" maxWidth="600px">
        <div className="info-text">
          <p>Yelken.pro, öğrencilerin sınav hazırlık sürecini daha verimli hale getirmek amacıyla tasarlanmış kapsamlı bir platformdur.</p>
          <p>Çalışma takibi, hedef planlama, topluluk etkileşimi ve performans analizi gibi araçlarla öğrencilerin motivasyonunu artırmayı hedefliyoruz.</p>
        </div>
      </Modal>
      <Modal isOpen={contactOpen} onClose={() => setContactOpen(false)} title="İletişim" maxWidth="600px">
        <div className="info-text">
          <p><strong>E-posta:</strong> destek@yelken.pro</p>
          <p><strong>Telefon:</strong> +90 544 225 6334</p>
          <p><strong>Adres:</strong> Kızılırmak Mah. 1443 Cad. No: 25-B/8 1071 Plaza Çankaya / Ankara</p>
        </div>
      </Modal>
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Gizlilik Politikası" maxWidth="600px">
        <div className="info-text">
          <p>Kişisel verileriniz Firebase'in güvenli sunucularında şifrelenmiş olarak saklanır. GDPR ve KVKK uyumludur.</p>
          <p>Detaylı bilgi için: destek@yelken.pro</p>
        </div>
      </Modal>
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Şartlar ve Koşullar" maxWidth="600px">
        <div className="info-text">
          <p>Yelken.pro'yu kullanarak bu şartları kabul etmiş olursunuz. Platform bilgilendirme amaçlıdır.</p>
          <p>Detaylı bilgi için: destek@yelken.pro</p>
        </div>
      </Modal>

      {/* Onboarding */}
      <AnimatePresence>
        {showOnboarding && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="modal-content" initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} style={{ textAlign: 'center' }}>
              <h2 className="text-gradient" style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>
                {onboardingSteps[currentStep].title}
              </h2>
              <p className="text-secondary" style={{ marginBottom: 'var(--space-6)' }}>
                {onboardingSteps[currentStep].description}
              </p>
              <div className="flex justify-center gap-2 mb-6">
                {onboardingSteps.map((_, i) => (
                  <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: currentStep === i ? 'var(--accent-primary)' : 'var(--border-subtle)' }} />
                ))}
              </div>
              <div className="flex justify-between gap-3">
                <button className="btn btn-danger btn-sm" onClick={() => handleOnboardingClose(true)}>Bir Daha Gösterme</button>
                <button className="btn btn-secondary btn-sm" onClick={() => handleOnboardingClose(false)}>Atla</button>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  if (currentStep === onboardingSteps.length - 1) handleOnboardingClose(false);
                  else setCurrentStep((s) => s + 1);
                }}>
                  {currentStep === onboardingSteps.length - 1 ? 'Başla!' : 'İleri'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MainLayout;